/**
 * DepartmentFilter Component
 * Multi-select filter for departments
 *
 * Options are `{ id, name }` — live `Department` rows, mirroring Reports. Selection and
 * `onChange` carry `Department.id` uuids; only the label rendered to the user is the name.
 * `GET /api/okrs?departmentIds=<name>` is a 422, so toggling by name here would break the
 * filter outright.
 */

import React from 'react';

interface DepartmentOption {
  id: string;
  name: string;
}

interface DepartmentFilterProps {
  availableDepartments: DepartmentOption[];
  selectedDepartments: string[];
  onChange: (departmentIds: string[]) => void;
}

export const DepartmentFilter: React.FC<DepartmentFilterProps> = ({
  availableDepartments,
  selectedDepartments,
  onChange,
}) => {
  const handleToggle = (departmentId: string) => {
    if (selectedDepartments.includes(departmentId)) {
      onChange(selectedDepartments.filter((d) => d !== departmentId));
    } else {
      onChange([...selectedDepartments, departmentId]);
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-stone-700">
        Department
      </label>
      <div className="max-h-40 overflow-y-auto space-y-1 border border-stone-200 rounded-lg p-2">
        {availableDepartments.length === 0 ? (
          <div className="text-sm text-stone-500 text-center py-4">
            No departments available
          </div>
        ) : (
          availableDepartments.map((department) => {
            const isSelected = selectedDepartments.includes(department.id);
            return (
              <label
                key={department.id}
                className="flex items-center gap-2 px-2 py-2 rounded hover:bg-stone-50 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleToggle(department.id)}
                  className="w-4 h-4 text-primary-600 border-stone-300 rounded focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-stone-900">{department.name}</span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
};
